import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

// ─── Types (mirrors backend services/checkMateService.js response) ────────────

interface HourlySlot {
  time: string;
  planet: string;
  note: string;
}

interface DailyTiming {
  todayPlanet: string;
  tomorrowPlanet: string;
  todayRating: "good_day" | "neutral_day" | "caution_day";
  bestDealWindow: string;
  hourlySlots: HourlySlot[];
}

interface ScanData {
  scanId: string;
  astro: {
    person1: { dayPlanet: string; element: string; lifePath: number };
    person2: { dayPlanet: string; element: string; lifePath: number };
    hasBirthTime: boolean;
  };
  gauges: { trustScore: number; wealthSynergy: number; redFlags: number };
  labels: { redFlagSeverity: string; wealthSynergyNote: string | null };
  verdict: "go" | "caution" | "no-go";
  verdictCopy: { headline: string; body: string; principle: string };
  dailyTiming: DailyTiming;
}

// ─── Animation variants ───────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.07,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

// ─── Shared UI atoms ──────────────────────────────────────────────────────────

const SectionCard: React.FC<{
  title: string;
  subtitle?: string;
  accent?: string;
  index?: number;
  children: React.ReactNode;
}> = ({ title, subtitle, accent = "#C084FC", index = 0, children }) => (
  <motion.div
    custom={index}
    variants={fadeUp}
    initial="hidden"
    animate="visible"
    className="bg-white border border-[#EEF2FF] rounded-3xl p-5 sm:p-6 md:p-8 mb-5 shadow-sm"
  >
    <div className="flex items-center gap-2 mb-1">
      <span
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ background: accent }}
      />
      <h3
        className="text-[10px] md:text-xs font-semibold tracking-[0.2em] uppercase"
        style={{ color: accent }}
      >
        {title}
      </h3>
    </div>
    {subtitle && (
      <p
        className="text-[11px] md:text-xs mb-5 ml-3.5"
        style={{ color: "#9D8FA8" }}
      >
        {subtitle}
      </p>
    )}
    <div className={subtitle ? "" : "mt-4"}>{children}</div>
  </motion.div>
);

const GaugeBar: React.FC<{
  label: string;
  value: number;
  color: string;
}> = ({ label, value, color }) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(value), 100);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <div>
      <div className="flex justify-between items-baseline mb-2 gap-2">
        <span
          className="text-[11px] md:text-xs font-semibold uppercase tracking-wide"
          style={{ color: "#6A548A" }}
        >
          {label}
        </span>
        <span
          className="text-sm md:text-base font-light flex-shrink-0"
          style={{ color: "#3D2B5A" }}
        >
          {value}%
        </span>
      </div>
      <div
        className="h-2.5 md:h-3 rounded-full overflow-hidden"
        style={{ background: "#F7F2FB" }}
      >
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${width}%`, background: color }}
        />
      </div>
    </div>
  );
};

const VERDICT_STYLE: Record<
  ScanData["verdict"],
  { bg: string; border: string; text: string; icon: string; label: string }
> = {
  go: {
    bg: "#F0FDF4",
    border: "#86EFAC",
    text: "#166534",
    icon: "🟢",
    label: "Go",
  },
  caution: {
    bg: "#FFFBEB",
    border: "#FDE68A",
    text: "#92400E",
    icon: "🟡",
    label: "Caution",
  },
  "no-go": {
    bg: "#FFF1F2",
    border: "#FDA4AF",
    text: "#BE123C",
    icon: "🔴",
    label: "No-Go",
  },
};

const TagBadge: React.FC<{
  text: string;
  color?: "purple" | "blue" | "rose" | "green" | "amber";
}> = ({ text, color = "purple" }) => {
  const styles: Record<string, string> = {
    purple: "bg-[#F7F2FB] text-[#6D4C92] border-[#C084FC]/30",
    blue: "bg-[#F0F9FF] text-[#0369A1] border-[#7DD3FC]/30",
    rose: "bg-[#FFF1F2] text-[#BE123C] border-[#FDA4AF]/30",
    green: "bg-[#F0FDF4] text-[#166534] border-[#86EFAC]/30",
    amber: "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]/30",
  };
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] md:text-[11px] font-semibold border tracking-wide ${styles[color]}`}
    >
      {text}
    </span>
  );
};

const TIMING_RATING_STYLE: Record<
  DailyTiming["todayRating"],
  { label: string; color: "green" | "amber" | "rose" }
> = {
  good_day: { label: "วันดี", color: "green" },
  neutral_day: { label: "วันกลาง ๆ", color: "amber" },
  caution_day: { label: "วันควรระวัง", color: "rose" },
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CheckMateResult: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<ScanData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("checkMateScan");
    if (!raw) {
      setError("ไม่พบข้อมูลการสแกน");
      return;
    }
    try {
      setData(JSON.parse(raw));
    } catch {
      setError("โหลดผลการสแกนไม่สำเร็จ");
    }
  }, []);

  const handleNewScan = () => {
    sessionStorage.removeItem("checkMateScan");
    navigate("/checkmate");
  };

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "#F7F2FB" }}
      >
        <div className="text-center">
          <p className="text-red-500 text-sm mb-4">{error}</p>
          <button
            onClick={() => navigate("/checkmate")}
            className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#C084FC] to-[#7DD3FC] text-white text-xs font-semibold"
          >
            เริ่มสแกน
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div
          className="w-8 h-8 rounded-full border-2 animate-spin"
          style={{ borderColor: "#C084FC33", borderTopColor: "#C084FC" }}
        />
      </div>
    );
  }

  const { gauges, labels, verdict, verdictCopy, dailyTiming, astro } = data;
  const vStyle = VERDICT_STYLE[verdict];
  const timingStyle = TIMING_RATING_STYLE[dailyTiming.todayRating];

  return (
    <div className="min-h-screen w-full bg-white">
      <div className="h-1 w-full" />

      <div className="min-h-screen w-full px-4 sm:px-6 py-8 sm:py-12">
        {/* Adjusted maximum width to create a centered, mobile-like stream configuration on desktop */}
        <div className="w-full max-w-2xl mx-auto">
          {/* Header nav - Swapped layouts to make Back button sit cleanly on the right side */}
          {/* Header nav */}
          <div className="mb-8 relative flex items-center justify-center min-h-[40px]">
            {/* Centered Title */}
            <div className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
              <span
                className="text-[10px] md:text-xs tracking-[0.2em] uppercase font-medium"
                style={{ color: "#9D8FA8" }}
              >
                สแกน Check-Mate
              </span>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
            </div>

            {/* Absolute Right Positioned Back Button */}
            <button
              onClick={handleNewScan}
              className="absolute right-0 inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-[#E5E7EB] bg-white px-4 py-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.1em] sm:tracking-[0.15em] text-[#6A548A] shadow-sm hover:bg-[#F7F2FB] transition flex-shrink-0"
            >
              ← Back
            </button>
          </div>

          {/* ── RESPONSIVE STREAMLINED LAYOUT ── */}
          {/* Kept structured as a single stacked view reminiscent of high-end mobile UI formats */}
          <div className="space-y-5">
            {/* Verdict Hero Section */}
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="rounded-3xl p-6 sm:p-8 text-center shadow-sm border"
              style={{ background: vStyle.bg, borderColor: vStyle.border }}
            >
              <div className="text-4xl sm:text-5xl mb-4">{vStyle.icon}</div>
              <p
                className="text-lg sm:text-xl font-semibold mb-3 leading-relaxed tracking-wide"
                style={{ color: vStyle.text }}
              >
                {verdictCopy.headline}
              </p>
              <p
                className="text-xs sm:text-sm leading-relaxed max-w-md mx-auto"
                style={{ color: "#5D544D" }}
              >
                {verdictCopy.body}
              </p>
              <p
                className="text-[10px] md:text-xs italic mt-5"
                style={{ color: "#9D8FA8" }}
              >
                {verdictCopy.principle}
              </p>
            </motion.div>

            {/* ── GAUGES ── */}
            <SectionCard
              title="มาตรวัดทั้ง 3"
              subtitle="เคมีของดวงดาวที่ให้คะแนนด้านความไว้วางใจ เงิน และความขัดแย้ง"
              accent="#C084FC"
              index={1}
            >
              <div className="space-y-6">
                <GaugeBar
                  label="🛡 คะแนนความไว้วางใจ"
                  value={gauges.trustScore}
                  color="#C084FC"
                />
                <GaugeBar
                  label="💰 การเสริมกันด้านความมั่งคั่ง"
                  value={gauges.wealthSynergy}
                  color="#F59E0B"
                />
                <GaugeBar
                  label="⚠ สัญญาณเตือน"
                  value={gauges.redFlags}
                  color="#EF4444"
                />
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <TagBadge text={labels.redFlagSeverity} color="rose" />
                {labels.wealthSynergyNote && (
                  <TagBadge text={labels.wealthSynergyNote} color="amber" />
                )}
              </div>
            </SectionCard>

            {/* ── DAILY TIMING LOCK ── */}
            <SectionCard
              title="ล็อกจังหวะเวลารายวัน"
              subtitle="ช่วงเวลาของดวงดาวในวันนี้สำหรับคู่นี้"
              accent="#6D4C92"
              index={2}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5 pb-4 border-b border-[#EEF2FF]">
                <div>
                  <p
                    className="text-[10px] md:text-[11px] uppercase tracking-wide"
                    style={{ color: "#9D8FA8" }}
                  >
                    ดาวผู้ปกครองวันนี้
                  </p>
                  <p
                    className="text-sm md:text-base font-medium"
                    style={{ color: "#3D2B5A" }}
                  >
                    {dailyTiming.todayPlanet}
                  </p>
                </div>
                <div className="flex items-start sm:items-center sm:justify-center">
                  <TagBadge
                    text={timingStyle.label}
                    color={timingStyle.color}
                  />
                </div>
                <div className="col-span-2 sm:col-span-1 sm:text-right">
                  <p
                    className="text-[10px] md:text-[11px] uppercase tracking-wide"
                    style={{ color: "#9D8FA8" }}
                  >
                    ช่วงเวลาที่ดีที่สุดสำหรับการตกลงธุรกิจ
                  </p>
                  <p
                    className="text-sm md:text-base font-medium"
                    style={{ color: "#3D2B5A" }}
                  >
                    {dailyTiming.bestDealWindow}
                  </p>
                </div>
              </div>
              <div className="space-y-2.5 max-h-[320px] overflow-y-auto pr-1">
                {dailyTiming.hourlySlots.map((slot, i) => (
                  <div
                    key={i}
                    className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-1 xs:gap-4 rounded-2xl p-3.5 border transition-colors hover:bg-[#F2EAFA]"
                    style={{ background: "#F7F2FB", borderColor: "#EEF2FF" }}
                  >
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span
                        className="text-[11px] md:text-xs font-semibold"
                        style={{ color: "#6D4C92" }}
                      >
                        {slot.time}
                      </span>
                      <span
                        className="text-[10px] md:text-[11px]"
                        style={{ color: "#9D8FA8" }}
                      >
                        {slot.planet} hour
                      </span>
                    </div>
                    <span
                      className="text-[11px] md:text-xs"
                      style={{ color: "#5D544D" }}
                    >
                      {slot.note}
                    </span>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* ── ASTRO SNAPSHOT ── */}
            <SectionCard
              title="ภาพรวมดวงชะตา"
              subtitle="ดาวประจำวัน ธาตุ และเส้นทางชีวิตของแต่ละคน"
              accent="#C084FC"
              index={3}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[astro.person1, astro.person2].map((p, i) => (
                  <div
                    key={i}
                    className="rounded-2xl p-4 border"
                    style={{ background: "#F7F2FB", borderColor: "#EEF2FF" }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] md:text-[10px] font-bold flex-shrink-0"
                        style={{
                          background: "linear-gradient(135deg,#C084FC,#6D4C92)",
                        }}
                      >
                        {i === 0 ? "1" : "2"}
                      </div>
                      <p
                        className="text-[10px] md:text-xs font-semibold"
                        style={{ color: "#6A548A" }}
                      >
                        Person {i + 1}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p
                        className="text-[11px] md:text-xs"
                        style={{ color: "#9D8FA8" }}
                      >
                        Planet:{" "}
                        <span
                          style={{ color: "#5D544D" }}
                          className="font-medium"
                        >
                          {p.dayPlanet}
                        </span>
                      </p>
                      <p
                        className="text-[11px] md:text-xs"
                        style={{ color: "#9D8FA8" }}
                      >
                        Element:{" "}
                        <span
                          style={{ color: "#5D544D" }}
                          className="font-medium"
                        >
                          {p.element}
                        </span>
                      </p>
                      <p
                        className="text-[11px] md:text-xs"
                        style={{ color: "#9D8FA8" }}
                      >
                        Life Path:{" "}
                        <span
                          style={{ color: "#5D544D" }}
                          className="font-medium"
                        >
                          {p.lifePath}
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>

          {/* Footer actions */}
          <div className="flex justify-center mt-10 pb-4">
            <button
              onClick={handleNewScan}
              className="w-full sm:w-auto py-3.5 px-12 rounded-full bg-gradient-to-r from-[#C084FC] to-[#7DD3FC] text-xs font-semibold uppercase tracking-[0.15em] text-white shadow-lg transition-all duration-200 hover:opacity-90 transform hover:-translate-y-0.5"
            >
              🛡 New Check-Mate Scan
            </button>
          </div>

          <div
            className="text-center mt-3 pb-8 text-[10px] md:text-[11px] tracking-wide"
            style={{ color: "#9D8FA8" }}
          >
            <span
              className="inline-block border-t w-10 sm:w-16 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
            not a judgment of character — only planetary chemistry
            <span
              className="inline-block border-t w-10 sm:w-16 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckMateResult;
